import { useEffect } from "react";
import { motion } from "motion/react";
import { LoaderCircle } from "lucide-react";

import { type Conversation, type Message } from "shared/apiClient";
import { AssessmentLights, ConversationPhase } from "shared/types";
import { cn, getText, type Noticeboard } from "shared/lib";

import { animationVariants } from "./lib";
import { SubdivisionType, useGetSubdivisionType } from "shared/hooks";
import { useSkipAnalysisLayoutAnimations } from "@/hooks";

import { Separator } from "shared/components";
import { NextStepItems } from "@/components/nextStepItems";
import { SubdivisionPanel } from "@/components/subdivision";
import { MultiLingualDisclaimer } from "@/components/analysisLayout/multiLingualDisclaimer";

export interface NextStepsProps {
  assessmentLights: AssessmentLights;
  conversation: Conversation | undefined | null;
  messages: Array<Message>;
  noticeboardData: Noticeboard | undefined | null;
  printable?: boolean;
}

export function NextSteps({
  assessmentLights,
  conversation,
  messages,
  noticeboardData,
  printable,
}: NextStepsProps) {
  const [skipAnimations, setSkipAnimations] = useSkipAnalysisLayoutAnimations();

  useEffect(() => {
    setTimeout(() => {
      setSkipAnimations(true);
    }, 1000);
  }, [setSkipAnimations]);

  const reportGenerating = [
    ConversationPhase.ready_for_assessment,
    ConversationPhase.conv_routing,
  ].includes(conversation?.phase as any);

  const subdivisionType = useGetSubdivisionType(conversation, noticeboardData);

  const state = conversation?.address_data?.state?.toUpperCase();

  const showSubdivisionPanel =
    !!subdivisionType &&
    subdivisionType !== SubdivisionType["n/a"] &&
    state === "VIC" &&
    assessmentLights !== AssessmentLights.Prohibited;

  return (
    <div className={cn("flex flex-col gap-10", printable && "gap-5")}>
      <div className="overflow-hidden">
        <motion.div
          className="flex [transform-origin:top_left] flex-col gap-1"
          variants={animationVariants}
          initial={!skipAnimations && "hidden"}
          animate={["show", "opacity"]}
        >
          <h2 className="text-textPrimary text-heading2xl">
            {getText("Next steps")}
          </h2>

          <p className="text-bodylg text-textSubdued">
            {getText(
              "What we recommend you do next based on your permit and application requirements.",
            )}
          </p>
        </motion.div>
      </div>

      <div className="overflow-hidden">
        {reportGenerating ? (
          <div className="text-textPrimary flex gap-1">
            <LoaderCircle className="min-h-5 min-w-5 animate-spin" />
            <p className="text-bodylg">
              {getText(
                "Your recommended next steps will appear here once your permit and application requirements have finished generating.",
              )}
            </p>
          </div>
        ) : (
          <motion.div
            className={cn(
              "flex [transform-origin:top_left] flex-col gap-10",
              printable && "gap-5",
            )}
            variants={animationVariants}
            initial={!skipAnimations && "hidden"}
            animate={["show", "opacity"]}
          >
            <div className="empty:hidden" id="content-header-portal" />

            {showSubdivisionPanel && (
              <>
                <SubdivisionPanel subdivisionType={subdivisionType} />

                <Separator />
              </>
            )}

            <div className="flex flex-col gap-3">
              {showSubdivisionPanel && (
                <h2 className="text-headinglg text-textPrimary">
                  What to do first
                </h2>
              )}

              <div className={cn("flex flex-col gap-10", printable && "gap-5")}>
                <NextStepItems
                  assessmentLights={assessmentLights}
                  conversation={conversation}
                  isSubdivision={showSubdivisionPanel}
                />
              </div>
            </div>

            <div className="empty:hidden" id="content-footer-portal" />

            <MultiLingualDisclaimer />
          </motion.div>
        )}
      </div>
    </div>
  );
}
